import { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row, Card, CardBody, Button } from "reactstrap";
import { toast } from "react-toastify";

import Breadcrumbs from "components/Common/Breadcrumb";
import SelectToggleButton from "components/Common/SelectToggleButton";
import GlobalInputSearch from "components/Common/GlobalInputSearch";

import {
  usePermissions,
  useRole,
  useRolePermissions,
  useAssignPermissionsToRole,
} from "hooks/useRolePermission";
import ButtonCheckboxList from "components/Common/ButtonCheckboxList";

const RolePermissionList = () => {
  document.title = "Permission List"

  const { roleSlug } = useParams()
  const navigate = useNavigate()
  const [search, setSearch] = useState("")
  const [pendingAssignedIds, setPendingAssignedIds] = useState([])

  // Fetch role info
  const { data: role } = useRole(roleSlug)
  const roleName = role?.data?.name || ""
  const isSystem = role?.data?.is_system || false

  // Fetch all permissions
  const { data: permissionsResponse } = usePermissions({
    paginate: false,
  })
  const permissions = permissionsResponse?.data || []

  // Fetch role-specific permissions
  const { data: rolePermsResponse, refetch: refetchRolePerms } =
    useRolePermissions(roleSlug, { paginate: false })
  const rolePerms = rolePermsResponse?.data || []

  // Initialize pendingAssignedIds from role's existing permissions
  useEffect(() => {
    if (role?.data?.permissions?.length) {
      setPendingAssignedIds(role.data.permissions.map(p => p.id))
    }
  }, [role])

  // Mutations
  const assignMutation = useAssignPermissionsToRole(roleSlug)
  const savePermissions = async () => {
    try {
      const assignedIds = rolePerms.map(p => p.id)

      const toAssign = pendingAssignedIds.filter(
        id => !assignedIds.includes(id)
      )

      await assignMutation.mutateAsync(toAssign)

      toast.success("Permissions updated successfully")
      refetchRolePerms()
    } catch {
      toast.error("Failed to update permissions")
    }
  }

  // Filtered permissions based on search
  const filteredPermissions = useMemo(() => {
    if (!search.trim()) return permissions
    return permissions.filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase())
    )
  }, [permissions, search])

  // Group permissions by module
  const grouped = useMemo(() => {
    const groups = {}
    filteredPermissions?.forEach(p => {
      const module = p.module?.name || "Other"
      if (!groups[module]) groups[module] = []
      groups[module].push(p)
    })
    return groups
  }, [filteredPermissions])

  const allPermissionIds = filteredPermissions.map(p => p.id)

  // Select/Deselect toggle handler
  const handleAllToggle = ids => {
    const allSelected = ids.every(id => pendingAssignedIds.includes(id))

    if (allSelected) {
      // Deselect all
      setPendingAssignedIds(prev => prev.filter(id => !ids.includes(id)))
    } else {
      // Select all
      setPendingAssignedIds(prev => {
        const newSelected = [...prev]
        ids.forEach(id => {
          if (!newSelected.includes(id)) newSelected.push(id)
        })
        return newSelected
      })
    }
  }

  if (isSystem) return navigate("/401", { replace: true })

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs
          title="Role Permission"
          breadcrumbItem={`Permissions for ${roleName || "Role"}`}
        />

        <Row>
          <Col lg="12">
            <Card>
              {/* Header with role name and Select All */}
              <CardBody className="border-bottom d-flex align-items-center">
                <h5 className="mb-0 card-title flex-grow-1">
                  Permissions for:{" "}
                  <span className="text-blue-600">{roleName}</span>
                </h5>
                <div className="flex-shrink-0 d-flex align-items-center gap-2">
                  <SelectToggleButton
                    ids={allPermissionIds}
                    selectedIds={pendingAssignedIds}
                    onToggle={handleAllToggle}
                  />
                </div>
              </CardBody>

              <CardBody>
                <Row>
                  <Col sm={4}>
                    <GlobalInputSearch value={search} onChange={setSearch} />
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col sm={12}>
                    {Object.entries(grouped).map(([module, perms]) => {
                      const moduleIds = perms.map(p => p.id)
                      return (
                        <div key={module} className="mb-4">
                          <div className="d-flex align-items-center justify-content-between mb-1">
                            <div>
                              <strong>{module}</strong>
                            </div>
                            <SelectToggleButton
                              ids={moduleIds}
                              selectedIds={pendingAssignedIds}
                              onToggle={handleAllToggle}
                            />
                          </div>
                          <ButtonCheckboxList
                            items={perms}
                            selected={pendingAssignedIds}
                            onChange={setPendingAssignedIds}
                            valueKey="id"
                            labelKey="name"
                            showSelectAll={false}
                            showSelectionSummary={false}
                            isShowHeader={false}
                          />
                        </div>
                      )
                    })}

                    {Object.keys(grouped).length === 0 && (
                      <p className="text-center py-10 text-gray-500">
                        No permissions found.
                      </p>
                    )}

                    <div className="d-flex justify-content-end mt-4">
                      <Button color="success" onClick={savePermissions}>
                        Save Changes{assignMutation.isPending && "..."}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default RolePermissionList
